import { supabase } from '@/integrations/supabase/client';
import { EstimateExportData, CategoryGroup, getFinalLineItemAmount } from './estimateExportUtils';
import { getDisplayDescription } from '@/utils/lineItemDisplayHelpers';

export interface ChangeOrderExportData extends EstimateExportData {
  isChangeOrder: boolean;
  baseVersion?: any;
  changes?: VersionChanges;
  lineItems?: any[]; // Add lineItems property for comprehensive view
}

export interface VersionChanges {
  added: any[];
  removed: any[];
  modified: any[];
  baseTotal: number;
  newTotal: number;
  totalDifference: number;
}

export interface ChangedItem {
  component_id: string;
  description: string;
  changeType: 'added' | 'removed' | 'modified';
  oldQuantity?: number;
  newQuantity?: number;
  oldUnitCost?: number;
  newUnitCost?: number;
  oldTotal?: number;
  newTotal?: number;
  quantity?: number;
  unit_cost?: number;
  total_cost?: number;
}

export const calculateVersionChanges = (baseItems: any[], compareItems: any[]): VersionChanges => {
  const changes: VersionChanges = {
    added: [],
    removed: [],
    modified: [],
    baseTotal: 0,
    newTotal: 0,
    totalDifference: 0
  };

  // Calculate totals
  changes.baseTotal = baseItems.reduce((sum, item) => sum + getFinalLineItemAmount(item), 0);
  changes.newTotal = compareItems.reduce((sum, item) => sum + getFinalLineItemAmount(item), 0);
  changes.totalDifference = changes.newTotal - changes.baseTotal;

  // Create maps for easier comparison
  const baseItemMap = new Map();
  baseItems.forEach(item => {
    const key = `${item.component_type}-${item.component_id}`;
    baseItemMap.set(key, item);
  });

  const compareItemMap = new Map();
  compareItems.forEach(item => {
    const key = `${item.component_type}-${item.component_id}`;
    compareItemMap.set(key, item);
  });

  // Find added and modified items
  compareItems.forEach(compareItem => {
    const key = `${compareItem.component_type}-${compareItem.component_id}`;
    const baseItem = baseItemMap.get(key);
    
    if (!baseItem) {
      // Item was added
      changes.added.push({
        ...compareItem,
        description: getDisplayDescription(compareItem),
        changeType: 'added'
      });
    } else {
      // Check if item was modified - handle both snapshot and database formats
      const baseQuantity = baseItem.quantity !== undefined ? baseItem.quantity : (baseItem.quantity_in_base_units || 0);
      const compareQuantity = compareItem.quantity !== undefined ? compareItem.quantity : (compareItem.quantity_in_base_units || 0);
      const baseUnitCost = getFinalLineItemAmount(baseItem) / Math.max(baseQuantity, 1);
      const compareUnitCost = getFinalLineItemAmount(compareItem) / Math.max(compareQuantity, 1);
      
      if (
        Math.abs(baseQuantity - compareQuantity) > 0.001 ||
        Math.abs(baseUnitCost - compareUnitCost) > 0.01
      ) {
        changes.modified.push({
          ...compareItem,
          description: getDisplayDescription(compareItem),
          changeType: 'modified',
          oldQuantity: baseQuantity,
          newQuantity: compareQuantity,
          oldUnitCost: baseUnitCost,
          newUnitCost: compareUnitCost,
          oldTotal: getFinalLineItemAmount(baseItem),
          newTotal: getFinalLineItemAmount(compareItem)
        });
      }
    }
  });

  // Find removed items
  baseItems.forEach(baseItem => {
    const key = `${baseItem.component_type}-${baseItem.component_id}`;
    if (!compareItemMap.has(key)) {
      changes.removed.push({
        ...baseItem,
        description: getDisplayDescription(baseItem),
        changeType: 'removed'
      });
    }
  });

  return changes;
};

export const fetchChangeOrderExportData = async (
  estimateId: string, 
  versionId?: string
): Promise<ChangeOrderExportData> => {
  console.log('[fetchChangeOrderExportData] Starting fetch for estimate:', estimateId, 'version:', versionId);
  
  // First get the standard export data
  const { fetchEstimateExportData } = await import('./estimateExportUtils');
  const baseExportData = await fetchEstimateExportData(estimateId, versionId);
  
  // Get company VAT information for proper calculations
  const isVatRegistered = baseExportData.company?.is_vat_registered ?? false;
  const companyVatRate = baseExportData.company?.["VAT Rate"] || 15;

  // Check if this is a change order
  if (!versionId) {
    console.log('[fetchChangeOrderExportData] No version ID provided, returning regular estimate');
    return { ...baseExportData, isChangeOrder: false };
  }

  const { data: currentVersion, error: versionError } = await supabase
    .from('estimate_versions')
    .select('*')
    .eq('id', versionId)
    .single();

  console.log('[fetchChangeOrderExportData] Version query result:', { currentVersion, versionError });

  if (versionError || currentVersion?.version_type !== 'change_order') {
    console.log('[fetchChangeOrderExportData] Not a change order or version not found:', {
      error: versionError,
      versionType: currentVersion?.version_type,
      isChangeOrder: currentVersion?.version_type === 'change_order'
    });
    
    // FIXED: For non-change order versions, ensure proper categorization
    // Re-fetch with live data to get proper component information
    if (currentVersion && currentVersion.version_type === 'estimate') {
      console.log('[fetchChangeOrderExportData] Re-fetching estimate version with enhanced data');
      const enhancedData = await fetchEstimateExportData(estimateId, versionId);
      return { ...enhancedData, isChangeOrder: false };
    }
    
    return { ...baseExportData, isChangeOrder: false };
  }

  console.log('[fetchChangeOrderExportData] Found change order version:', currentVersion.version_number);

  // FIXED: Use parent_version_id first, then fallback to lookup logic
  let baseVersion = null;
  
  // Method 1: Try to use parent_version_id if available
  if (currentVersion.parent_version_id) {
    console.log('[fetchChangeOrderExportData] Using parent_version_id:', currentVersion.parent_version_id);
    const { data: parentVersion, error: parentError } = await supabase
      .from('estimate_versions')
      .select('*')
      .eq('id', currentVersion.parent_version_id)
      .single();
    
    if (!parentError && parentVersion) {
      baseVersion = parentVersion;
      console.log('[fetchChangeOrderExportData] Found parent version:', baseVersion.version_number);
    }
  }

  // Method 2: Fallback to finding latest approved version before this change order
  if (!baseVersion) {
    console.log('[fetchChangeOrderExportData] No parent version, searching for latest approved version');
    const { data: approvedVersion, error: approvedError } = await supabase
      .from('estimate_versions')
      .select('*')
      .eq('estimate_id', estimateId)
      .eq('status', 'approved')
      .lt('version_number', currentVersion.version_number)
      .order('version_number', { ascending: false })
      .limit(1)
      .single();

    if (!approvedError && approvedVersion) {
      baseVersion = approvedVersion;
      console.log('[fetchChangeOrderExportData] Found approved base version:', baseVersion.version_number);
    }
  }

  // Method 3: Final fallback to any approved version
  if (!baseVersion) {
    console.log('[fetchChangeOrderExportData] No approved version found, searching for any approved version');
    const { data: anyApprovedVersion, error: anyApprovedError } = await supabase
      .from('estimate_versions')
      .select('*')
      .eq('estimate_id', estimateId)
      .eq('status', 'approved')
      .order('version_number', { ascending: false })
      .limit(1)
      .single();

    if (!anyApprovedError && anyApprovedVersion) {
      baseVersion = anyApprovedVersion;
      console.log('[fetchChangeOrderExportData] Found any approved version:', baseVersion.version_number);
    }
  }

  // Method 4: Ultimate fallback to main version
  if (!baseVersion) {
    console.log('[fetchChangeOrderExportData] No approved version found, searching for main version');
    const estimate = await supabase
      .from('estimates')
      .select('main_version_id')
      .eq('id', estimateId)
      .single();

    const mainVersionId = estimate.data?.main_version_id;
    
    if (mainVersionId && mainVersionId !== versionId) {
      const { data: mainVersion, error: mainError } = await supabase
        .from('estimate_versions')
        .select('*')
        .eq('id', mainVersionId)
        .single();

      if (!mainError && mainVersion) {
        baseVersion = mainVersion;
        console.log('[fetchChangeOrderExportData] Found main version:', baseVersion.version_number);
      }
    }
  }

  if (!baseVersion) {
    console.warn('[fetchChangeOrderExportData] No base version found for change order - returning without changes');
    return { 
      ...baseExportData, 
      isChangeOrder: true,
      changes: {
        added: [],
        removed: [],
        modified: [],
        baseTotal: 0,
        newTotal: baseExportData.totalAmount || 0,
        totalDifference: baseExportData.totalAmount || 0
      }
    };
  }

  // Calculate changes between base and current version
  const baseItems = Array.isArray(baseVersion.line_items_snapshot) ? baseVersion.line_items_snapshot : [];
  const currentItems = Array.isArray(currentVersion.line_items_snapshot) ? currentVersion.line_items_snapshot : [];
  
  console.log('[fetchChangeOrderExportData] Calculating changes between versions');
  console.log('[fetchChangeOrderExportData] Base items:', baseItems.length);
  console.log('[fetchChangeOrderExportData] Current items:', currentItems.length);
  console.log('[fetchChangeOrderExportData] Sample base item structure:', baseItems[0]);
  console.log('[fetchChangeOrderExportData] Sample current item structure:', currentItems[0]);
  
  const changes = calculateVersionChanges(baseItems, currentItems);

  console.log('[fetchChangeOrderExportData] Changes calculated:', {
    added: changes.added.length,
    removed: changes.removed.length,
    modified: changes.modified.length,
    totalDifference: changes.totalDifference,
    baseTotal: changes.baseTotal,
    newTotal: changes.newTotal
  });

  // Debug: Log the calculation process
  console.log('[fetchChangeOrderExportData] Final amounts being set:', {
    changeOrderTaxAmount: isVatRegistered && companyVatRate > 0 ? (changes.totalDifference * (companyVatRate / 100)) : 0,
    changeOrderTotalAmount: changes.newTotal + (isVatRegistered && companyVatRate > 0 ? (changes.totalDifference * (companyVatRate / 100)) : 0),
    isVatRegistered,
    companyVatRate
  });

  // FIXED: Calculate proper tax and total amounts for change order
  const changeOrderTaxAmount = isVatRegistered && companyVatRate > 0 ? 
    (changes.totalDifference * (companyVatRate / 100)) : 0;
  const changeOrderTotalAmount = changes.newTotal + changeOrderTaxAmount;

  console.log('[fetchChangeOrderExportData] Returning change order data:', {
    isChangeOrder: true,
    hasBaseVersion: !!baseVersion,
    hasChanges: !!changes,
    baseVersionNumber: baseVersion?.version_number,
    currentVersionNumber: currentVersion.version_number
  });

  return {
    ...baseExportData,
    isChangeOrder: true,
    baseVersion,
    changes,
    // Override with correct change order amounts
    taxAmount: changeOrderTaxAmount,
    totalAmount: changeOrderTotalAmount
  };
};
